import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";
import { useCanAccess } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { useDeskAttentionCounts } from "../contacts/useDeskAttention";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { filterNavSections, isRouteActive, navSections } from "./navConfig";

interface SidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  onToggleCollapse,
}) => {
  const location = useLocation();
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const { canAccess: isAdmin } = useCanAccess({
    resource: "sales",
    action: "list",
  });
  const sections = useMemo(
    () => filterNavSections(navSections, Boolean(isAdmin)),
    [isAdmin],
  );
  const { needsWhatsAppReply: inboxCount } = useDeskAttentionCounts();

  return (
    <TooltipProvider delayDuration={100}>
      <aside
        className={`h-screen bg-card border-r flex flex-col justify-between transition-all duration-300 z-30 select-none ${
          isCollapsed ? "w-16" : "w-64"
        }`}
      >
        {/* Top Header Logo */}
        <div>
          <div className="h-16 px-4 flex items-center justify-between border-b bg-muted/20">
            <Link
              to="/"
              className="flex items-center gap-3 overflow-hidden no-underline"
            >
              <img
                className="[.light_&]:hidden h-7 w-7 object-contain"
                src={darkModeLogo}
                alt={title}
              />
              <img
                className="[.dark_&]:hidden h-7 w-7 object-contain"
                src={lightModeLogo}
                alt={title}
              />
              {!isCollapsed && (
                <div className="flex flex-col">
                  <span className="font-extrabold text-foreground text-sm tracking-tight leading-none">
                    {title}
                  </span>
                  <span className="text-[10px] font-semibold text-primary mt-0.5 flex items-center">
                    <ShieldCheck className="w-3 h-3 mr-0.5" /> Real Estate
                    Edition
                  </span>
                </div>
              )}
            </Link>

            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground hidden md:flex"
              onClick={onToggleCollapse}
              title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {isCollapsed ? (
                <ChevronRight className="w-4 h-4" />
              ) : (
                <ChevronLeft className="w-4 h-4" />
              )}
            </Button>
          </div>

          {/* Nav Sections */}
          <div className="p-2 space-y-4 overflow-y-auto max-h-[calc(100vh-8rem)]">
            {sections.map((section, idx) => (
              <div key={idx} className="space-y-1">
                {!isCollapsed && (
                  <h4 className="px-3 text-[10px] font-bold text-muted-foreground tracking-wider uppercase pt-2">
                    {section.title}
                  </h4>
                )}

                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isRouteActive(location.pathname, item.path);

                  const showInboxBadge =
                    item.path === "/inbox" && inboxCount > 0;

                  const linkContent = (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`relative flex items-center gap-3 px-3 py-2 rounded-lg text-xs transition-colors font-medium ${
                        active
                          ? "bg-primary/10 text-primary font-bold shadow-xs border-l-2 border-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      } ${isCollapsed ? "justify-center px-0" : ""}`}
                    >
                      <Icon
                        className={`w-4 h-4 shrink-0 ${active ? "text-primary" : "text-muted-foreground"}`}
                      />
                      {!isCollapsed && (
                        <span className="truncate flex-1">{item.label}</span>
                      )}
                      {showInboxBadge ? (
                        isCollapsed ? (
                          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-600" />
                        ) : (
                          <Badge className="h-4 min-w-4 px-1 rounded-full bg-red-600 text-white text-[9px] leading-none flex items-center justify-center">
                            {inboxCount > 99 ? "99+" : inboxCount}
                          </Badge>
                        )
                      ) : null}
                    </Link>
                  );

                  if (isCollapsed) {
                    return (
                      <Tooltip key={item.path}>
                        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                        <TooltipContent
                          side="right"
                          className="font-semibold text-xs"
                        >
                          {item.label}
                        </TooltipContent>
                      </Tooltip>
                    );
                  }

                  return linkContent;
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Footer Collapse Button */}
        <div className="p-2 border-t bg-muted/10 flex flex-col justify-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs text-muted-foreground flex items-center justify-center gap-2"
            onClick={onToggleCollapse}
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span>Collapse Navigation</span>
              </>
            )}
          </Button>
          {!isCollapsed && (
            <a
              href="https://www.blivix.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-muted-foreground/60 hover:text-primary transition-colors text-center block py-0.5 font-medium no-underline"
            >
              Powered by Blivix
            </a>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
};

export default Sidebar;
